import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, CheckCircle2, CloudOff, Search, Smartphone, Apple, Play, Users, ArrowRight, Radio, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { ShowCard } from "@/components/ShowCard";
import { CategoryChips } from "@/components/CategoryChips";
import { Loading, Empty } from "@/components/common";
import { PLAY_STORE_URL, APP_STORE_URL, openExternal } from "@/lib/appLinks";

export default function Discover() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [category, setCategory] = useState("All");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refPrize, setRefPrize] = useState(null);

  const load = async () => {
    setLoading(true); setError(false);
    try {
      const [s, c] = await Promise.all([api.get("/shows"), api.get("/categories")]);
      setShows(s.shows); setCats(c.categories);
    } catch { setError(true); } finally { setLoading(false); }
    api.get("/referrals/leaderboard").then((d) => setRefPrize(d.prize)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const prizeLabel = refPrize && ((refPrize.type === "cash" && refPrize.value)
    ? `$${Number(refPrize.value).toFixed(0)} cash`
    : (refPrize.type === "product" && refPrize.label) ? refPrize.label : null);

  const filtered = useMemo(() => {
    let list = shows.filter((s) => s.status === "live" || s.status === "scheduled");
    if (category !== "All") list = list.filter((s) => s.category === category);
    if (verifiedOnly) list = list.filter((s) => s.seller_verified);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((s) =>
      (s.title || "").toLowerCase().includes(q) ||
      (s.seller_name || "").toLowerCase().includes(q) ||
      (s.category || "").toLowerCase().includes(q)
    );
    return list;
  }, [shows, category, verifiedOnly, query]);

  const liveCount = shows.filter((s) => s.status === "live").length;
  const hasFilters = category !== "All" || verifiedOnly || query.trim() !== "";
  const clearFilters = () => { setCategory("All"); setVerifiedOnly(false); setQuery(""); };

  return (
    <div className="fade-up">
      {/* Top header */}
      <div className="disc-header">
        <div>
          <div className="disc-brand">
            <span className="ss-dot" style={{ marginRight: 8 }} />
            <span style={{ color: "var(--brand)" }}>Souq</span><span style={{ color: "var(--gold)" }}>Stream</span>
          </div>
          <div className="disc-sub" data-testid="live-count">{liveCount} shows live right now</div>
        </div>
        <div className="disc-searchbox" data-testid="discover-search">
          <Search size={18} className="ss-muted" />
          <input
            placeholder="Search shows, sellers, categories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="discover-search-input"
          />
        </div>
      </div>

      {/* Categories */}
      <CategoryChips categories={cats} selected={category} onSelect={setCategory} />

      {/* Refer & win promo */}
      <div
        className="ss-card"
        onClick={() => navigate("/refer")}
        data-testid="referral-promo-banner"
        style={{ marginTop: 16, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
                 cursor: "pointer", background: "var(--grad-hero)", color: "#fff", border: "none" }}
      >
        <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(255,255,255,.16)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Gift size={24} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>
            Refer &amp; win {prizeLabel || "monthly prizes"}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,.85)", marginTop: 2 }}>
            Invite friends who join &amp; shop — the top 5 referrers win every month.
          </div>
        </div>
        <span className="ss-btn ss-btn-gold ss-btn-sm" style={{ flexShrink: 0 }} data-testid="referral-promo-cta">
          Refer now <ArrowRight size={15} />
        </span>
      </div>

      {/* Hero */}
      <div className="ss-hero" style={{ marginTop: 16 }}>
        <div className="ss-live-badge"><span className="ss-dot" /> {liveCount} LIVE NOW</div>
        <h1>Shop live from Muslim sellers, worldwide</h1>
        <p>Bid on auctions, grab buy-it-now deals, and join live giveaways — all in real time, right from your screen.</p>
        <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
          <button className="ss-btn ss-btn-gold" onClick={() => navigate("/sell")} data-testid="hero-sell"><Video size={18} /> Start a live show</button>
        </div>
      </div>

      {/* Download banner */}
      <div className="ss-card dl-banner" data-testid="download-banner">
        <div className="dl-ic"><Smartphone size={26} /></div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Take SouqStream with you</div>
          <div className="ss-muted" style={{ fontSize: 14, marginTop: 2 }}>Sell live, bid, and check out from your phone. Download the app for iOS or Android.</div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="dl-store" onClick={() => (APP_STORE_URL ? openExternal(APP_STORE_URL) : navigate("/download"))} data-testid="dl-appstore"><Apple size={22} /><span><small>Download on the</small><b>App Store</b></span></button>
          <button className="dl-store" onClick={() => openExternal(PLAY_STORE_URL)} data-testid="dl-googleplay"><Play size={20} /><span><small>Get it on</small><b>Google Play</b></span></button>
        </div>
      </div>

      {/* Verified toggle */}
      <div style={{ marginTop: 20 }}>
        <button
          className={`ss-chip ${verifiedOnly ? "active" : ""}`}
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="verified-only-toggle"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <CheckCircle2 size={15} /> Verified sellers only
        </button>
      </div>

      <div className="ss-section-head">
        <h2>{verifiedOnly ? "Verified sellers" : category === "All" ? "Live & upcoming shows" : category}</h2>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <Empty icon={CloudOff} title="Failed to load feed" sub="Tap to retry.">
          <button className="ss-btn ss-btn-primary" onClick={load} data-testid="retry-button">Retry</button>
        </Empty>
      ) : filtered.length === 0 ? (
        <div className="ss-card fade-up" style={{ padding: "44px 28px", textAlign: "center" }} data-testid="discover-empty">
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "var(--brand)", display: "grid", placeItems: "center", margin: "0 auto 18px" }}>
            <Radio size={32} style={{ color: "var(--gold)" }} />
          </div>
          {hasFilters ? (
            <>
              <h3 style={{ fontSize: 22, fontWeight: 800 }}>No shows match your filters</h3>
              <p className="ss-muted" style={{ fontSize: 15, marginTop: 8, maxWidth: 460, marginInline: "auto", lineHeight: 1.6 }}>
                Try another category, or browse everything that's live and coming up.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 22 }}>
                <button className="ss-btn ss-btn-primary" onClick={clearFilters} data-testid="empty-browse-all">Browse all shows <ArrowRight size={16} /></button>
                <button className="ss-btn ss-btn-ghost" onClick={() => navigate("/sell")} data-testid="empty-start-show"><Video size={16} /> Start your own show</button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ fontSize: 22, fontWeight: 800 }}>No live shows right now</h3>
              <p className="ss-muted" style={{ fontSize: 15, marginTop: 8, maxWidth: 480, marginInline: "auto", lineHeight: 1.6 }}>
                Be the first to go live — or follow sellers so you get a notification the moment they start streaming.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 22 }}>
                <button className="ss-btn ss-btn-gold" onClick={() => navigate("/sell")} data-testid="empty-start-show"><Video size={16} /> Schedule your show</button>
                <button className="ss-btn ss-btn-ghost" onClick={() => navigate("/following")} data-testid="empty-follow-sellers"><Users size={16} /> Sellers you follow</button>
              </div>
              <p className="ss-muted" style={{ fontSize: 13, marginTop: 18 }}>
                Tip: open any seller's store and tap <b>Follow</b> to be notified when they go live.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="ss-grid grid-3" data-testid="shows-grid">
          {filtered.map((s) => <ShowCard key={s.id} show={s} />)}
        </div>
      )}
    </div>
  );
}
